// Building definitions. The settlement auto-places these as resources allow.
// Each building advances exactly one axis (the blueprint's economy / military /
// knowledge directions, plus population housing and raw gathering), so the
// card you pick each age steers which buildings get prioritized.

import type { ResourceId } from './resources';
import { BALANCE, scaleByLevel } from './balance';

export type BuildingId =
  | 'house'
  | 'lumberCamp'
  | 'quarry'
  | 'mine'
  | 'barracks'
  | 'school';

/** The direction a building pushes. Cards boost a matching direction. */
export type BuildingAxis = 'economy' | 'military' | 'knowledge' | 'population' | 'gather';

export interface BuildingDef {
  id: BuildingId;
  name: string;
  icon: string;
  color: number; // sprite tint, also used in build popups
  axis: BuildingAxis;
  desc: string;
  /** Base price (level 0 to 1). Each later level multiplies by costGrowth. */
  baseCost: Partial<Record<ResourceId, number>>;
  /** Passive resource production added per level. */
  produce?: { resource: ResourceId; perLevel: number };
  /** Contribution to the economy axis (global production multiplier) per level. */
  economyPerLevel?: number;
  /** Contribution to raw defense (military axis) per level. */
  defensePerLevel?: number;
  /** Contribution to the knowledge axis (tech factor) per level. */
  knowledgePerLevel?: number;
  /** Population cap added per level. */
  popCapPerLevel?: number;
  /** Auto-builder weight; higher means the settlement prefers it. */
  basePriority: number;
}

export const BUILDINGS: Record<BuildingId, BuildingDef> = {
  house: {
    id: 'house',
    name: 'House',
    icon: '🏠',
    color: 0xc98a5a,
    axis: 'population',
    desc: 'Shelters settlers, raising population and workforce.',
    baseCost: { wood: 8, stone: 4 },
    popCapPerLevel: 2,
    basePriority: 1.0,
  },
  lumberCamp: {
    id: 'lumberCamp',
    name: 'Lumber Camp',
    icon: '🪓',
    color: 0x6f9a4d,
    axis: 'gather',
    desc: 'Fells timber automatically.',
    baseCost: { wood: 6, stone: 6 },
    produce: { resource: 'wood', perLevel: 0.3 },
    basePriority: 0.9,
  },
  quarry: {
    id: 'quarry',
    name: 'Quarry',
    icon: '🧱',
    color: 0x8f97a0,
    axis: 'gather',
    desc: 'Cuts stone automatically.',
    baseCost: { wood: 10, stone: 8 },
    produce: { resource: 'stone', perLevel: 0.28 },
    basePriority: 0.85,
  },
  mine: {
    id: 'mine',
    name: 'Mine',
    icon: '⛏️',
    color: 0xb9772f,
    axis: 'economy',
    desc: 'Extracts ore and strengthens the whole economy.',
    baseCost: { stone: 16, wood: 10 },
    produce: { resource: 'ore', perLevel: 0.22 },
    economyPerLevel: 0.035,
    basePriority: 0.8,
  },
  barracks: {
    id: 'barracks',
    name: 'Barracks',
    icon: '🛡️',
    color: 0xb0494a,
    axis: 'military',
    desc: 'Trains defenders, raising the settlement defense.',
    baseCost: { wood: 14, stone: 14, ore: 3 }, // less ore-gated so early defense is attainable
    defensePerLevel: 5,
    basePriority: 0.85, // nudged up so defense keeps trickling in on any build path

  },
  school: {
    id: 'school',
    name: 'School',
    icon: '🏫',
    color: 0x5a78c9,
    axis: 'knowledge',
    desc: 'Produces knowledge and advances technology and defense.',
    baseCost: { wood: 12, stone: 12, ore: 4 },
    produce: { resource: 'knowledge', perLevel: 0.26 },
    knowledgePerLevel: 3,
    basePriority: 0.7,
  },
};

export const BUILDING_IDS = Object.keys(BUILDINGS) as BuildingId[];

/** Cost to take a building from `level` to `level + 1`, scaled by costGrowth. */
export function buildingCost(id: BuildingId, level: number): Partial<Record<ResourceId, number>> {
  const def = BUILDINGS[id];
  const out: Partial<Record<ResourceId, number>> = {};
  for (const [res, amount] of Object.entries(def.baseCost) as [ResourceId, number][]) {
    out[res] = scaleByLevel(amount, BALANCE.costGrowth, level);
  }
  return out;
}
